import { getBrowserLanguage, setDialog, setSession, setSettings, setSnackbar, stringAvatar } from "@shared/library";
import { languages } from "@shared/library/dist/static/language";
import axios, { AxiosError } from "axios";
import React from "react";
import { useNavigate } from 'react-router-dom';
import packageJson from "../../../../../package.json";
import config from '../../../../config';
import importer from '../../../../helpers/importer';
import './Header.scss';
//#region ICONS
import { Logout } from "@mui/icons-material";
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import LogoutIcon from '@mui/icons-material/Logout';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import { ReactComponent as AdminPanelIcon } from "./_icons/admin_panel.svg";
//#endregion
//#region COMPONENTS
import { Divider, Tooltip } from "@mui/material";
import Avatar from '@mui/material/Avatar';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import GestButton from "@shared/library/dist/components/gest/GestInputs/button/GestButton";
import {
    ContextMenuContent,
    Popover
} from '../../../gest';
//#endregion
//CONTEXTS
import { CntxConfig } from "../../../WebGest";

const Header = (props: { isOpened: boolean; }) => {
    //CONTEXTS USE
    const cntxConfig = React.useContext(CntxConfig)!;

    //#region STATIC DATA
    const lang = languages(cntxConfig.settings?.lang);
    const isOpened = props.isOpened;
    const navigate = useNavigate();
    const availableLangs = [
        { id: "it", name: "italian" },
        { id: "en", name: "english", iconName: "gb" },
        { id: "fr", name: "french" },
    ];
    //#endregion

    //#region STATES
    const anchorRef = React.useRef<HTMLButtonElement>(null);
    //FLAGS
    const [infoOpened, setInfoOpened] = React.useState(false);
    const [logoutLoading, setLogoutLoading] = React.useState(false);
    //#endregion

    //#region METHODS

    //#region API CALLS
    const logoutAPI = () => {
        setLogoutLoading(true);
        axios.delete(`${config.api.internal}/Auth/logout`, { withCredentials: true })
            .then(() => setSession(cntxConfig))
            .catch((err: AxiosError) => setSnackbar(cntxConfig, { message: "Errore durante chiusura della sessione corrente", severity: "error" }))
            .finally(() => setLogoutLoading(false));
    };

    const updateLanguageAPI = (lang: string) =>
        axios.patch(`${config.api.internal}/Account/Language/ChangeTo/${lang}`, undefined, { withCredentials: true })
            .then(() => {
                let settingsLS = JSON.parse(localStorage.getItem('settings') || '{}');

                settingsLS.lang = lang;
                setSettings(cntxConfig, settingsLS);
                localStorage.setItem('settings', JSON.stringify(settingsLS));
            })
            .catch((err: AxiosError) => setSnackbar(cntxConfig, { message: "Errore salvataggio della lingua scelta", severity: "error" }));
    //#endregion

    const handleClose = (event: Event | React.SyntheticEvent) => {
        if (
            anchorRef.current &&
            anchorRef.current.contains(event.target as HTMLElement)
        ) {
            return;
        }

        setInfoOpened(false);
    };

    const openLogoutDialog = () => {
        setDialog(cntxConfig, {
        content: <div className="flex-column" style={{ width: '400px', textAlign: 'center' }}>
            <img height="100" width="100" style={{ margin: 'auto' }} src={importer.img.require('exit.png')} alt="" />
            {lang.really_want_logout}
        </div>,
        footer:
            <GestButton
                icon={<Logout />}
                className="btn-danger"
                loading={logoutLoading}
                label={lang.logout}
                onclick={logoutAPI}
            />
    })};
    //#endregion

    React.useEffect(openLogoutDialog, [logoutLoading]);

    return (
        <nav id="gestNavbar" className={isOpened ? 'opened' : ''}>
            <Tooltip title={"Version " + packageJson.version}>
                <div
                    className="logoBox"
                    onClick={() => navigate("/")}
                    onAuxClick={event => {
                        if (event.button === 1) {
                            window.open(`/`, '_blank');
                        }
                    }}
                >
                    <img id="logo" src={importer.img.require("webGestLogo.svg")} alt="webGestLogo" loading="lazy" />
                </div>
            </Tooltip>

            <div className="utils">
                <div id="actions">
                    {(cntxConfig.session?.userData!.email || cntxConfig.session?.userData!.role === 'admin') &&
                        <>
                            {cntxConfig.session?.userData!.role === 'admin' &&
                                <GestButton
                                    iconBtn
                                    tooltip="Administration Panel"
                                    icon={<AdminPanelIcon height={30} width={30} />}
                                    onclick={() => navigate("/AdminPanel")}
                                />}

                            {localStorage.getItem('support') === '1' &&
                                <GestButton
                                    iconBtn
                                    tooltip="Support"
                                    icon={<HelpOutlineRoundedIcon />}
                                    onclick={() => window.open(config.platforms.support, '_blank')}
                                />}
                        </>}

                    <GestButton
                        iconBtn
                        tooltip="Logout"
                        icon={<LogoutIcon />}
                        onclick={openLogoutDialog}
                    />
                </div>

                <Divider orientation="vertical" style={{ margin: "0 10px", height: "40%" }} />

                <div style={{ width: '100%' }}>
                    <Popper
                        open={infoOpened}
                        anchorEl={anchorRef.current}
                        role={undefined}
                        placement="bottom-start"
                        transition
                        disablePortal
                    >
                        {({ TransitionProps }) => (
                            <Grow
                                {...TransitionProps}
                                style={{
                                    transformOrigin: 'right top',
                                }}
                            >
                                <Paper>
                                    <ClickAwayListener onClickAway={handleClose}>
                                        <></>
                                    </ClickAwayListener>
                                </Paper>
                            </Grow>
                        )}
                    </Popper>
                </div>

                <Popover
                    variant="ghost"
                    anchor="bottom-right"
                    arrow={true}
                    overflow={true}
                    placeholder={
                        <div className='account'>
                            <Avatar {...stringAvatar(`${cntxConfig.session?.userData!.firstname} ${cntxConfig.session?.userData!.lastname}`)} />
                            <div className="anag">
                                <h4>{`${cntxConfig.session?.userData!.firstname} ${cntxConfig.session?.userData!.lastname}`}</h4>
                                <h5>@{cntxConfig.session?.userData!.username}</h5>
                            </div>
                        </div>
                    }
                >
                    <ContextMenuContent
                        anchor="right"
                        options={[
                            {
                                leftIcon: <PublicRoundedIcon style={{ fill: "var(--primary)" }} />,
                                label: lang.language,
                                subs: availableLangs.map(l => ({
                                    label: lang.universal.languages[l.name as keyof typeof lang.universal.languages],
                                    onClick: () => updateLanguageAPI(l.id),
                                    leftIcon: <img style={{}} src={importer.flag.require(`${l.iconName ?? l.id}.svg`)} alt="" loading="lazy" />,
                                    rightIcon: cntxConfig.settings?.lang === l.id || (!cntxConfig.settings?.lang && getBrowserLanguage() === l.id) ? <CheckRoundedIcon /> : <></>,
                                }))
                            }
                        ]}
                    />
                </Popover>
            </div>
        </nav>
    );
};
export default React.memo(Header);